import { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useApolloClient } from "@apollo/client";
import {
  QUERY_GET_TRANSACTIONS,
  QUERY_GET_FINANCIAL_TOTALS,
} from "common/src/graphql/queries";
import DepositTransactions from "common/src/component/DepositTransactions";
import WithdrawTransactions from "common/src/component/WithdrawTransactions";
import FinancialTotals from "common/src/component/FinancialTotals";
import { ContentTop } from "common/src/component/ContentTop";
import config from "../config.json";
import { isMobileOnly } from "react-device-detect";
import {
  AccountContainer,
  PageContent,
  PageContentWrapper,
  TabContent,
  TransactionsButtonWrapper,
  TransactionButton,
} from "common/src/styledComponents/AccountPage";
import {
  StyledButton,
  StyledFormGroup,
  StyledSelect,
} from "common/src/styledComponents/CommonStyledComponents";
import { StyledLimitWrapper } from "common/src/styledComponents/StyledLimits";
import { toast } from "react-toastify";
import { MUTATION_PROMPT_REVIEW_DIALOG } from "common/src/graphql/mutations";

const Transactions = ({
  playerCurrency,
  server,
  pages,
  titles,
  serverSetup,
  skin,
  player,
}) => {
  const { t } = useTranslation("");
  const apolloClient = useApolloClient();

  const [depositHistory, setDepositHistory] = useState({});
  const [withdrawHistory, setWithdrawHistory] = useState({});
  const [financialTotal, setFinancialTotal] = useState({});
  const [showAllTransactions, setshowAllTransactions] = useState(false);
  const [reviewTransactionsDialogPeriod, setReviewTransactionsDialogPeriod] =
    useState("");
  const isUK =
    player && player.country === "gb"
      ? true
      : serverSetup?.geoIP?.isoCode?.toLowerCase() === "gb"
        ? true
        : false;

  useEffect(() => {
    // console.log(titles)
    document.title = titles["transactions"];
  }, [titles]);

  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
      });
    };
    scrollToTop();
  }, []);

  useEffect(() => {
    const getFinancialsTotal = async () => {
      const { loading, errors, data } = await apolloClient.query({
        query: QUERY_GET_FINANCIAL_TOTALS,
      });
      if (loading) return <h3>Loading ...</h3>;
      if (errors) return <h1>`Error: ${errors}`</h1>;
      //console.log("total " + data);
      setFinancialTotal(data.getFinancials);
      //winners = recentWinners;
      return null;
    };
    async function getTransactionsHistory(transactionType) {
      const { loading, errors, data } = await apolloClient.query({
        query: QUERY_GET_TRANSACTIONS,
        variables: { transactionType: transactionType },
      });
      if (loading) return <h3>Loading ...</h3>;
      if (errors) return <h1>`Error: ${errors}`</h1>;
      //console.log("transaction " + data.getTransactions.transactions);
      if (transactionType === "deposit") {
        setDepositHistory(data.getTransactions);
      } else {
        setWithdrawHistory(data.getTransactions);
      }
      return null;
    }
    getFinancialsTotal();
    getTransactionsHistory("deposit");
    getTransactionsHistory("withdrawal");
  }, [apolloClient]);

  const handleReviewDialogPeriod = async () => {
    if (reviewTransactionsDialogPeriod === "") return;

    const { data, errors } = await apolloClient.mutate({
      mutation: MUTATION_PROMPT_REVIEW_DIALOG,
      variables: {
        duration: parseInt(reviewTransactionsDialogPeriod),
      },
    });

    if (data && data.setRts12 && data.setRts12.ok === true) {
      setReviewTransactionsDialogPeriod("");
      toast.success(t("label_success"), {
        position: "bottom-center",
      });
    }
    if (errors) {
      toast.error(t(errors[0].message), {
        position: "bottom-center",
      });
      return false;
    }

    return true;
  };

  return (
    <>
      <ContentTop
        serverSetup={serverSetup}
        showProfileMenu={!isMobileOnly}
        pages={pages}
        skin={skin}
        isMyAccount={"label_transactions"}
      />
      <PageContent account="true" nobg="true">
        <PageContentWrapper>
          {isUK && (
            <StyledLimitWrapper reviewTransactions verification>
              <StyledFormGroup
                col={"col-md-8"}
                margin={skin === "rac" ? "" : "m-0"}
              >
                <label>{t("label_choose_time_period_for_review")}</label>
                <StyledSelect
                  onChange={(e) =>
                    setReviewTransactionsDialogPeriod(e.target.value)
                  }
                >
                  <option value="" disabled selected>
                    {t("label_choose_time_period_for_review")}
                  </option>
                  <option value="1">{t("message_show_on_1_month")}</option>
                  <option value="2">{t("message_show_on_2_months")}</option>
                  <option value="3">{t("message_show_on_3_months")}</option>
                  <option value="4">{t("message_show_on_4_months")}</option>
                  <option value="5">{t("message_show_on_5_months")}</option>
                  <option value="6">{t("message_show_on_6_months")}</option>
                </StyledSelect>
              </StyledFormGroup>
              <StyledButton
                pub={skin === "pub"}
                selfexcludion
                disabled={reviewTransactionsDialogPeriod === ""}
                onClick={() => handleReviewDialogPeriod()}
              >
                {t("label_save", "Save")}
              </StyledButton>
            </StyledLimitWrapper>
          )}
          <AccountContainer>
            <TabContent>
              <>
                <TransactionsButtonWrapper>
                  <Trans i18nKey="label_transaction_description"></Trans>
                  <TransactionButton
                    onClick={() => setshowAllTransactions(!showAllTransactions)}
                  >
                    {!showAllTransactions
                      ? t("label_view_all", "View all")
                      : t("label_show_total", "Show total")}
                  </TransactionButton>
                </TransactionsButtonWrapper>
                {!showAllTransactions && (
                  <FinancialTotals
                    financialTotal={financialTotal}
                    playerCurrency={playerCurrency}
                    server={server}
                    skin={config.skin}
                  />
                )}
                {showAllTransactions && (
                  <>
                    <DepositTransactions
                      playerCurrency={playerCurrency}
                      depositHistory={depositHistory}
                      server={server}
                      skin={config.skin}
                    ></DepositTransactions>
                    <WithdrawTransactions
                      playerCurrency={playerCurrency}
                      withdrawHistory={withdrawHistory}
                      server={server}
                      skin={config.skin}
                    />
                  </>
                )}
              </>
            </TabContent>
          </AccountContainer>
        </PageContentWrapper>
      </PageContent>
    </>
  );
};

export default Transactions;
